import React, { forwardRef } from 'react';
import OutsideClickHandler from 'react-outside-click-handler';
import { Button, Titles, ContentWrapper } from '../../../../common/TabsStyled';
import IDHelpers from '@/core/helpers/IDHelper';
import {
  ContainerOptions,
  ContainerTab,
  CardButtonInstitutional,
  ContainerCardsInstitutional,
  TextCard,
  CardsIcons
} from '../styles';
import { Icon } from '@/presentation/components';
import eyeIcon from '../../../../../../assets/icons/eye-icon.svg';
import { GlobalStyles } from '@/presentation/components/LightBox';
import VisualizerPDF from './VisualizerPDF';
import leftdate from '../../../../../../assets/icons/left-date.svg';
import rightdate from '../../../../../../assets/icons/right-date.svg';
import useIsMobile from '@/presentation/hooks/useIsMobile';
import { handleDownload } from './utils';

interface Props {
  titles: any[];
  activeTab: number;
  setActiveTab: (tab: number) => void;
}

const TabsInstitutional = forwardRef<HTMLDivElement, Props>(
  ({ titles, activeTab, setActiveTab }) => {
    const containerRef = React.useRef<HTMLDivElement>(null);
    const [showPdf, setShowPdf] = React.useState(false);
    const [namePdf, setNamePdf] = React.useState('');
    const [filePdf, setFilePdf] = React.useState('');
    const [currentFiles, setCurrentFiles] = React.useState<File[]>([]);
    const isMobile = useIsMobile(true);

    const ViewPdf = (name: string, file: string) => {
      setShowPdf(true);
      setNamePdf(name);
      setFilePdf(file);
    };

    React.useEffect(() => {
      if (titles[activeTab]) {
        setCurrentFiles(titles[activeTab].files);
      } else if (titles.length > 0) {
        setCurrentFiles(titles[0].files);
      }
    }, [activeTab, titles]);

    const MoveTabs = (direction: 'left' | 'right') => {
      if (direction === 'left') {
        setActiveTab(activeTab - 1);
      } else if (direction === 'right') {
        setActiveTab(activeTab + 1);
      }
      if (containerRef.current) {
        const item = containerRef.current.querySelector(
          '.active-tabs'
        ) as HTMLDivElement;

        const itemWidth = item?.offsetWidth || 0;
        containerRef.current.scrollBy({
          left: direction === 'right' ? itemWidth : -itemWidth,
          behavior: 'smooth'
        });
      }
    };

    return (
      <ContainerTab data-testid="cmp-tab">
        <Titles data-testid="container-ref">
          {isMobile && activeTab !== 0 && (
            <button
              type="button"
              data-testid="previous-tab-button"
              style={{
                backgroundColor: '#FFF',
                width: 48,
                height: 48,
                borderRadius: 100,
                left: '4px',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                position: 'absolute',
                zIndex: 1
              }}
              onClick={() => MoveTabs('left')}
            >
              <Icon customized size={20} display="flex" alignItems="center">
                <img src={leftdate} alt="Scroll left" />
              </Icon>
            </button>
          )}
          <ContainerOptions ref={containerRef}>
            {titles.map(({ name }, index) => {
              const order = index;
              return (
                <div
                  data-testid="child-tabInstitutional"
                  key={IDHelpers.generateID()}
                  className={activeTab === order ? 'active' : ''}
                >
                  <Button
                    data-testid="cmp-button-activetab"
                    className={`tabs ${
                      activeTab === order ? 'active-tabs' : ''
                    }`}
                    onClick={() => {
                      setActiveTab(order);
                    }}
                  >
                    {name}
                  </Button>
                </div>
              );
            })}
          </ContainerOptions>
          {isMobile && activeTab !== titles.length - 1 && (
            <button
              type="button"
              data-testid="next-tab-button"
              style={{
                backgroundColor: '#FFF',
                width: 48,
                height: 48,
                borderRadius: 100,
                right: '4px',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                position: 'absolute',
                zIndex: 1
              }}
              onClick={() => MoveTabs('right')}
            >
              <Icon customized size={20} display="flex" alignItems="center">
                <img src={rightdate} alt="Scroll right" />
              </Icon>
            </button>
          )}
        </Titles>
        <ContentWrapper>
          <ContainerCardsInstitutional>
            {currentFiles.map((e: any) => (
              <CardButtonInstitutional key={IDHelpers.generateID()}>
                <TextCard>{e.title}</TextCard>
                <CardsIcons>
                  <div>
                    <button
                      type="button"
                      onClick={() => handleDownload(e.nameFile)}
                    >
                      <Icon name="download" size={18} color="#5B36F2" />
                    </button>
                  </div>
                  {!isMobile && (
                    <div>
                      <button
                        type="button"
                        onClick={() => ViewPdf(e.title, e.nameFile)}
                      >
                        <Icon customized size={18}>
                          <img src={eyeIcon} alt="other" />
                        </Icon>
                      </button>
                    </div>
                  )}
                </CardsIcons>
              </CardButtonInstitutional>
            ))}
          </ContainerCardsInstitutional>
        </ContentWrapper>
        {showPdf && (
          <>
            <GlobalStyles />
            <div
              data-testid="overlay"
              style={{
                background: 'rgb(40 48 68 / 60%)',
                position: 'fixed',
                left: 0,
                right: 0,
                top: 0,
                bottom: 0,
                zIndex: 99,
                height: '100%'
              }}
            >
              <OutsideClickHandler onOutsideClick={() => setShowPdf(!showPdf)}>
                <VisualizerPDF
                  name={namePdf}
                  onClose={() => setShowPdf(false)}
                  filePdf={filePdf}
                />
              </OutsideClickHandler>
            </div>
          </>
        )}
      </ContainerTab>
    );
  }
);

TabsInstitutional.displayName = 'TabsInstitutional';

export default TabsInstitutional;
